import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  AlertColor,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { API_BASE_URL } from '../../services/api';

interface StatusHistory {
  history_id: number;
  booking_id: number;
  status: string;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  createdBy?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface BookingStatusHistoryProps {
  bookingId: number;
  currentStatus: string;
  onStatusUpdate?: () => void;
}

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'work_in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const BookingStatusHistory: React.FC<BookingStatusHistoryProps> = ({ bookingId, currentStatus, onStatusUpdate }) => {
  const [history, setHistory] = useState<StatusHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const ucfirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as AlertColor,
  });

  const fetchStatusHistory = async () => {
    try {
      setLoading(true);
      console.log('Fetching status history for booking:', bookingId);
      const response = await axios.get(`${API_BASE_URL}/bookings/${bookingId}/history`);
      console.log('Status history response:', response.data);
      
      // Handle different response formats
      let historyData: any[] = [];
      
      if (Array.isArray(response.data)) {
        // Direct array response
        historyData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data.history)) {
        // Nested response: { data: { history: [...] } }
        historyData = response.data.data.history;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Response: { data: [...] }
        historyData = response.data.data;
      } else if (response.data && Array.isArray(response.data.history)) {
        // Response: { history: [...] }
        historyData = response.data.history;
      } else {
        console.warn('Unexpected status history response format:', response.data);
        historyData = [];
      }
      
      console.log('Extracted history data:', historyData);
      setHistory(historyData);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching status history:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers,
        },
      });
      setError('Failed to load status history. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bookingId) {
      fetchStatusHistory();
    }
  }, [bookingId]);

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value);
  };

  const handleOpenDialog = () => {
    setStatus('');
    setNotes('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    if (!status) return;

    try {
      console.log('Updating status for booking:', bookingId, { status, notes });
      await axios.post(`${API_BASE_URL}/bookings/${bookingId}/history`, {
        status,
        notes: notes || undefined,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setSnackbar({
        open: true,
        message: 'Status updated successfully',
        severity: 'success',
      });

      // Refresh the history and call the onStatusUpdate callback if provided
      await fetchStatusHistory();
      if (onStatusUpdate) {
        onStatusUpdate();
      }

      setOpenDialog(false);
    } catch (err) {
      console.error('Error updating status:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update status. Please try again.',
        severity: 'error',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'work_in_progress':
        return 'info';
      case 'confirmed':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'work_in_progress':
        return <AccessTimeIcon color="info" />;
      case 'confirmed':
        return <CheckCircleIcon color="primary" />;
      default:
        return <AccessTimeIcon color="action" />;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (e) {
      return dateString;
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mt: 3,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <HistoryIcon /> Status History
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : !Array.isArray(history) || history.length === 0 ? (
        <Typography variant="body2" color="textSecondary" align="center" py={3}>
          No status history available
        </Typography>
      ) : (
        <List sx={{ width: '100%' }}>
          {history.map((item) => (
            <ListItem key={item.history_id} alignItems="flex-start" sx={{ px: 0 }}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'transparent' }}>
                  {getStatusIcon(item.status)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" component="span">
                      Status changed to
                    </Typography>
                    <Chip
                      label={ucfirst(item.status.replace(/_/g, ' '))}
                      color={getStatusColor(item.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.primary"
                      display="block"
                    >
                      {item.notes}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {formatDateTime(item.created_at)}
                      </Typography>
                      {item.createdBy && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <PersonIcon fontSize="inherit" />
                          {`${item.createdBy.first_name} ${item.createdBy.last_name}`}
                        </Typography>
                      )}
                    </Box>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* Update Status Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Booking Status</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={status}
                label="Status"
                onChange={handleStatusChange}
                fullWidth
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes (Optional)"
              multiline
              rows={3}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this status update"
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!status}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default BookingStatusHistory;
